import { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { saveAction } from "@/lib/saveAction";
import { sortTasksDefault } from "@/lib/taskSort";
import { dueLabel } from "@/lib/format";
import { StatusPill, PriorityIcon } from "@/components/shared/Pills";
import { AssigneePicker } from "@/components/form/AssigneePicker";
import { CheckCircle2, ChevronDown, ListChecks, Plus, CalendarDays } from "lucide-react";

function TaskRow({ t, users, onToggle, onAssign, onOpen }) {
  const done = t.status === "Completed" || t.status === "Cancelled";
  return (
    <div className="group flex items-center gap-2 rounded-lg border border-slate-100 bg-white px-2.5 py-2 hover:border-slate-200" data-testid={`linked-task-${t.id}`}>
      <button type="button" onClick={() => onToggle(t)} title={done ? "Reopen" : "Mark complete"} data-testid={`linked-task-toggle-${t.id}`}
        className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
          done ? "border-emerald-500 text-emerald-500" : "border-slate-300 text-transparent hover:border-emerald-500 hover:text-emerald-500")}>
        <CheckCircle2 className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => onOpen(t)} className="min-w-0 flex-1 text-left" data-testid={`linked-task-open-${t.id}`}>
        <p className={cn("truncate text-sm font-medium", done ? "text-slate-400 line-through" : "text-slate-800")}>{t.title}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-slate-400">
          {!done && <PriorityIcon priority={t.priority} />}
          <StatusPill status={t.status} />
          {t.due_date && <span className={cn("flex items-center gap-1", t.is_overdue && !done && "font-semibold text-red-600")}><CalendarDays className="h-3 w-3" />{dueLabel(t.due_date)}</span>}
          {t.subtask_total > 0 && <span className="flex items-center gap-1"><ListChecks className="h-3 w-3" />{t.subtask_done}/{t.subtask_total}</span>}
        </div>
      </button>
      <div onClick={(e) => e.stopPropagation()} className="shrink-0">
        <AssigneePicker multi value={t.assigned_to_ids || []} onChange={(v) => onAssign(t, v)} users={users} testid={`linked-task-assign-${t.id}`} size="h-6 w-6" floatRight />
      </div>
    </div>
  );
}

// Tasks linked to a parent record (campaign, collab, print order, voucher, opportunity).
export function LinkedTasksPanel({ parentType, parentId, parentLabel, prefill = {}, title = "Tasks" }) {
  const { users } = useAuth();
  const { openTask, dataVersion } = useUI();
  const [rows, setRows] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const load = useCallback(() => client.get("/actions", { params: { action_type: "Task", parent_type: parentType, parent_id: parentId } })
    .then(({ data }) => setRows(data)).catch(() => setRows([])), [parentType, parentId]);
  useEffect(() => { load(); }, [load, dataVersion]);

  const add = () => openTask({ prefill: { action_type: "Task", status: "To Do", priority: "Medium", ...prefill, parent_type: parentType, parent_id: parentId, parent_label: parentLabel }, onSuccess: load });
  const toggle = async (t) => {
    const done = t.status === "Completed" || t.status === "Cancelled";
    setRows((r) => r.map((x) => (x.id === t.id ? { ...x, status: done ? "To Do" : "Completed" } : x)));
    try {
      if (done) await saveAction(t, { ...t, status: "To Do" });
      else await client.post(`/actions/${t.id}/complete`, {});
      toast.success(done ? "Task reopened" : "Task done");
    } catch (e) { toast.error("Could not update task"); }
    load();
  };
  const assign = async (t, ids) => {
    setRows((r) => r.map((x) => (x.id === t.id ? { ...x, assigned_to_ids: ids } : x)));
    try { await saveAction(t, { ...t, assigned_to_ids: ids, assigned_to_id: ids[0] || null }); } catch (e) { toast.error("Could not reassign"); }
    load();
  };
  const isDone = (t) => t.status === "Completed" || t.status === "Cancelled";
  const open = sortTasksDefault((rows || []).filter((t) => !isDone(t)));
  const done = (rows || []).filter(isDone);
  const rowProps = { users, onToggle: toggle, onAssign: assign, onOpen: (t) => openTask(t, load) };

  return (
    <div data-testid={`linked-tasks-${parentType}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><ListChecks className="h-3.5 w-3.5" />{title}{rows && <span className="font-medium text-slate-400">({open.length} open)</span>}</span>
        <button type="button" onClick={add} className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-sky-600 hover:bg-sky-50" data-testid={`linked-tasks-add-${parentType}`}>
          <Plus className="h-3.5 w-3.5" />Add task
        </button>
      </div>
      {rows === null ? <p className="py-2 text-xs text-slate-400">Loading…</p> : (
        <div className="space-y-1.5">
          {!open.length && !done.length && <p className="rounded-lg border border-dashed border-slate-200 py-3 text-center text-xs text-slate-400" data-testid="linked-tasks-empty">No tasks yet</p>}
          {open.map((t) => <TaskRow key={t.id} t={t} {...rowProps} />)}
          {done.length > 0 && (
            <>
              <button type="button" onClick={() => setShowDone((v) => !v)} className="flex items-center gap-1 pt-1 text-xs font-medium text-slate-400 hover:text-slate-600" data-testid="linked-tasks-completed-toggle">
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", !showDone && "-rotate-90")} />Completed ({done.length})
              </button>
              {showDone && done.map((t) => <TaskRow key={t.id} t={t} {...rowProps} />)}
            </>
          )}
        </div>
      )}
    </div>
  );
}
